import { useState, useEffect } from 'react';
import { UserAccountStatusEnum, TransactionCategoryEnum } from '@/enums';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelRequired } from '@/components/hoc';
import { useRequestGetUsers } from '@/hooks/httpRequest';
import type { TFormData } from '..';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { Check, ChevronsUpDown, Loader2, Search, User } from 'lucide-react';
import { cn } from '@/lib/utils';

// Simple debounce hook
function useDebounce<T>(value: T, delay: number): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debouncedValue;
}

const FormCreateOrEditTransactionBodyInputUser = () => {
	const { control, setValue } = useFormContext<TFormData>();
	const category = useWatch({ control, name: 'category' });

	// Using explicit cast or path for useWatch to avoid TS errors on nested paths
	const selectedUserId = useWatch({ control, name: 'data.userId' }) as string | undefined;

	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearchTerm = useDebounce(searchTerm, 500);

	const isInAppPurchase = category === TransactionCategoryEnum.IN_APP_PURCHASE;

	// Search query
	const { users, isLoading } = useRequestGetUsers(
		{
			accountStatus: [UserAccountStatusEnum.ACTIVE],
			limit: 10,
			emailOrUsername: debouncedSearchTerm || undefined
		},
		isInAppPurchase && open
	);

	// Selected user query (to show name when ID is present but not in search results)
	const { users: selectedUsers } = useRequestGetUsers(
		{
			ids: selectedUserId ? [selectedUserId] : undefined
		},
		!!selectedUserId && isInAppPurchase
	);

	const selectedUser = selectedUsers?.[0];

	useEffect(() => {
		if (!open) {
			setSearchTerm('');
		}
	}, [open]);

	if (!isInAppPurchase) {
		return null;
	}

	const handleSelect = (userId: string) => {
		setValue('data.userId', userId, { shouldValidate: true });
		setOpen(false);
	};

	return (
		<div className="flex flex-col gap-1.5">
			<LabelRequired className="text-[11px] md:text-xs">Utilisateur</LabelRequired>

			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className={cn(
							'w-full justify-between bg-muted/30 focus:bg-background transition-colors h-9 md:h-10 font-normal px-3',
							!selectedUserId && 'text-muted-foreground'
						)}
					>
						{selectedUser ? (
							<span className="flex items-center gap-2 truncate">
								<User className="h-3.5 w-3.5 opacity-70" />
								<span className="font-medium">{selectedUser.username}</span>
								<span className="text-xs text-muted-foreground">({selectedUser.email})</span>
							</span>
						) : (
							'Rechercher un utilisateur...'
						)}
						<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
					<div className="flex flex-col w-full bg-popover text-popover-foreground">
						{/* Search Input */}
						<div className="flex items-center border-b px-3">
							<Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
							<input
								className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
								placeholder="Rechercher par nom ou email..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
							/>
							{isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin opacity-50" />}
						</div>

						{/* Results List */}
						<div className="max-h-[200px] overflow-y-auto overflow-x-hidden p-1">
							{!isLoading && users.length === 0 && (
								<div className="py-6 text-center text-sm text-muted-foreground">Aucun utilisateur trouvé.</div>
							)}

							{users.map(user => (
								<button
									type="button"
									key={user._id}
									onClick={() => handleSelect(user._id)}
									className={cn(
										'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors text-left',
										selectedUserId === user._id && 'bg-accent text-accent-foreground'
									)}
								>
									<Check className={cn('mr-2 h-4 w-4', selectedUserId === user._id ? 'opacity-100' : 'opacity-0')} />
									<div className="flex flex-col flex-1 min-w-0">
										<span className="font-medium truncate">{user.username}</span>
										<span className="text-xs text-muted-foreground truncate">{user.email}</span>
									</div>
								</button>
							))}
						</div>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export default FormCreateOrEditTransactionBodyInputUser;
